// frontend/src/pages/PerfilPessoal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import PropertyCard from '../components/PropertyCard';
import { Link, useNavigate } from 'react-router-dom';
import ListaIncorporadoras from '../components/ListaIncorporadoras';
import SugestoesAside from '../components/connections/SugestoesAside';

// ✅ Importação do Novo Componente Minimalista
import BrazaoGoogleRating from '../components/avaliacao/BrazaoAvaliacoesButton';

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  onSnapshot,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  MapPin,
  BadgePercent,
  Phone,
  Pencil,
  ShieldCheck,
} from 'lucide-react';

import IconeComparador from '../components/IconeComparador';
import EncerrarAnuncioModal from '../components/imoveis/EncerrarAnuncioModal';

// ✅ Service do contador de placas
import { contarImoveisComPlaca } from '../services/dashboardService';

const formatarEspecialidade = (valor) => {
  switch (valor) {
    case 'residencial':
      return 'Imóveis residenciais';
    case 'comercial':
      return 'Imóveis comerciais';
    case 'alto_padrao':
      return 'Imóveis de alto padrão';
    case 'empreendimentos':
      return 'Empreendimentos';
    case 'rural':
      return 'Imóveis rurais';
    default:
      return valor;
  }
};

// =========================
// Ordenação (Perfil)
// =========================
function toMillis(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (typeof v?.toDate === 'function') {
    const d = v.toDate();
    return d instanceof Date ? d.getTime() : 0;
  }
  return 0;
}

function statusRank(p) {
  const s = String(p?.statusNegocio || 'ativo').toLowerCase();
  return s === 'ativo' ? 0 : 1;
}

/* =========================
   Destaques (helpers)
   - lê do users/{uid}.siteBuilder.conteudo.(imoveis|destaques).ids
========================= */
function resolveModelId(raw) {
  const v = String(raw || '').toLowerCase();
  if (v.includes('imobili')) return 'imobiliaria';
  return 'corretor';
}

function getFeaturedIdsFromUserDoc(userData) {
  const modelId = resolveModelId(
    userData?.siteBuilder?.modelId ||
      userData?.siteConfig?.modelId ||
      userData?.modelId ||
      'corretor'
  );

  // ⚠️ no seu imoveisService:
  // - corretor -> siteBuilder.conteudo.imoveis.ids
  // - imobiliaria -> siteBuilder.conteudo.destaques.ids
  const ids =
    modelId === 'imobiliaria'
      ? userData?.siteBuilder?.conteudo?.destaques?.ids
      : userData?.siteBuilder?.conteudo?.imoveis?.ids;

  const arr = Array.isArray(ids) ? ids : [];
  return arr.filter(Boolean).map((x) => String(x));
}

/* =========================
   Avaliações - Helpers/UI
========================= */
function clamp01(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.min(10, Math.max(0, x));
}

function calcMedia(crit) {
  const vals = [
    clamp01(crit?.etica),
    clamp01(crit?.compromisso),
    clamp01(crit?.comunicacao),
    clamp01(crit?.postura),
    clamp01(crit?.tempoResposta),
  ];
  const sum = vals.reduce((a, b) => a + b, 0);
  return sum / vals.length;
}

function Pill({ children, tone = 'neutral' }) {
  const tones = {
    neutral:
      'bg-slate-100/80 text-slate-700 border-slate-200 shadow-sm shadow-slate-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 shadow-sm shadow-blue-100',
    emerald:
      'bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm shadow-emerald-100',
    amber:
      'bg-amber-50 text-amber-800 border-amber-200 shadow-sm shadow-amber-100',
  };

  return (
    <span
      className={[
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border',
        'backdrop-blur-sm',
        tones[tone] || tones.neutral,
      ].join(' ')}
    >
      {children}
    </span>
  );
}

function CriteriaBar({ label, value }) {
  const v = clamp01(value);
  const pct = Math.round((v / 10) * 100);

  return (
    <div className="py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-slate-700">{label}</div>
        <div className="text-sm font-semibold text-slate-900 tabular-nums">
          {v.toFixed(1)}
        </div>
      </div>

      <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-blue-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function EmptyStateAvaliacoes({ nome }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
          <span className="text-lg">🛡️</span>
        </div>

        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">
            Ainda não há avaliações
          </div>
          <div className="mt-1 text-sm text-slate-600">
            Quando seus sócios avaliarem seu perfil, a média e os comentários
            aparecem aqui.
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Pill tone="neutral">Dica: feche 1 parceria e peça feedback</Pill>
            <Pill tone="emerald">Meta rápida: 5 validações</Pill>
          </div>

          <div className="mt-4 text-xs text-slate-500">
            Perfil: <span className="font-medium">{nome || 'Corretor'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Painel: Avaliações (read-only no PerfilPessoal)
========================= */
function AvaliacoesPanel({ perfilId, nomePerfil, onVoltar }) {
  const [loading, setLoading] = useState(true);
  const [itens, setItens] = useState([]);

  useEffect(() => {
    if (!perfilId) return;

    const ref = collection(db, 'avaliacoes', perfilId, 'itens');
    const q = query(ref, orderBy('criadoEm', 'desc'), limit(50));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setItens(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('Erro ao ler avaliações:', err);
        setItens([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [perfilId]);

  const stats = useMemo(() => {
    if (!itens?.length) {
      return {
        count: 0,
        media: 0,
        breakdown: {
          etica: 0,
          compromisso: 0,
          comunicacao: 0,
          postura: 0,
          tempoResposta: 0,
        },
      };
    }

    const sum = {
      etica: 0,
      compromisso: 0,
      comunicacao: 0,
      postura: 0,
      tempoResposta: 0,
      media: 0,
    };

    itens.forEach((r) => {
      const c = r?.criterios || {};
      sum.etica += clamp01(c.etica);
      sum.compromisso += clamp01(c.compromisso);
      sum.comunicacao += clamp01(c.comunicacao);
      sum.postura += clamp01(c.postura);
      sum.tempoResposta += clamp01(c.tempoResposta);
      sum.media += Number(r?.media ?? calcMedia(c));
    });

    const count = itens.length;
    return {
      count,
      media: sum.media / count,
      breakdown: {
        etica: sum.etica / count,
        compromisso: sum.compromisso / count,
        comunicacao: sum.comunicacao / count,
        postura: sum.postura / count,
        tempoResposta: sum.tempoResposta / count,
      },
    };
  }, [itens]);

  const isReferencia = useMemo(
    () => stats.count >= 5 && stats.media >= 9.0,
    [stats]
  );

  const mediaLabel = stats.media ? stats.media.toFixed(1) : '—';

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={onVoltar}
            className="text-sm font-medium text-slate-700 hover:text-slate-950 transition"
          >
            ← Voltar
          </button>

          <div className="h-4 w-px bg-slate-200" />

          <h3 className="text-base font-semibold text-slate-900">Avaliações</h3>

          {isReferencia && (
            <Pill tone="blue">
              <ShieldCheck size={14} />
              Referência
            </Pill>
          )}

          <Pill tone="neutral">{stats.count} validações</Pill>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 grid lg:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="h-3 w-24 bg-slate-100 rounded" />
            <div className="mt-3 h-8 w-28 bg-slate-100 rounded" />
            <div className="mt-3 h-3 w-32 bg-slate-100 rounded" />
          </div>
          <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="h-3 w-20 bg-slate-100 rounded" />
            <div className="mt-4 space-y-3">
              {[1, 2, 3, 4, 5].map((k) => (
                <div key={k} className="h-10 bg-slate-100 rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      ) : itens.length === 0 ? (
        <div className="mt-4">
          <EmptyStateAvaliacoes nome={nomePerfil} />
          <div className="mt-3 text-xs text-slate-500">
            Sócios avaliam no perfil público do corretor (quando há sociedade
            aceita).
          </div>
        </div>
      ) : (
        <>
          <div className="mt-4 grid lg:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 relative overflow-hidden">
              <div className="absolute -top-20 -right-24 w-56 h-56 rounded-full bg-gradient-to-br from-emerald-100 to-blue-100 blur-2xl opacity-70" />
              <div className="relative">
                <div className="text-xs text-slate-500">Média geral</div>

                <div className="mt-1 flex items-end gap-2">
                  <div className="text-4xl font-semibold text-slate-900 tabular-nums">
                    {mediaLabel}
                  </div>
                  <div className="text-sm text-slate-500 pb-2">/ 10</div>
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <Pill tone={stats.media >= 9 ? 'emerald' : 'neutral'}>
                    <ShieldCheck size={14} />
                    {stats.media >= 9 ? 'Excelente' : 'Em evolução'}
                  </Pill>
                  <Pill tone="neutral">Últimas 50</Pill>
                </div>

                <div className="mt-3 text-xs text-slate-500">
                  Base: {stats.count} validaç{stats.count === 1 ? 'ão' : 'ões'}
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">Critérios</div>
                <Pill tone="neutral">0–10</Pill>
              </div>

              <div className="mt-3 divide-y divide-slate-100">
                <CriteriaBar label="Ética" value={stats.breakdown.etica} />
                <CriteriaBar
                  label="Compromisso"
                  value={stats.breakdown.compromisso}
                />
                <CriteriaBar
                  label="Comunicação"
                  value={stats.breakdown.comunicacao}
                />
                <CriteriaBar label="Postura" value={stats.breakdown.postura} />
                <CriteriaBar
                  label="Tempo de resposta"
                  value={stats.breakdown.tempoResposta}
                />
              </div>
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-900">
                Comentários
              </div>
              <Pill tone="neutral">
                <ShieldCheck size={14} /> {stats.count}
              </Pill>
            </div>

            <div className="mt-4 space-y-3">
              {itens.map((r) => {
                const c = r?.criterios || {};
                const media = Number(r?.media ?? calcMedia(c));

                return (
                  <article
                    key={r.id}
                    className="rounded-2xl border border-slate-200 p-4 hover:shadow-sm transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={r?.avaliadorFoto || '/placeholder-user.jpg'}
                          alt={r?.avaliadorNome || 'Avaliador'}
                          className="w-10 h-10 rounded-full object-cover border border-slate-200"
                        />
                        <div>
                          <div className="text-sm font-semibold text-slate-900">
                            {r?.avaliadorNome || 'Sócio'}
                          </div>
                          <div className="text-xs text-slate-500">
                            {r?.vinculo === 'socio' ? 'Sócio' : 'Corretor'}
                          </div>
                        </div>
                      </div>

                      <div className="text-right">
                        <div className="text-sm font-semibold text-slate-900 tabular-nums">
                          {media.toFixed(1)}
                          <span className="text-xs text-slate-500">/10</span>
                        </div>
                        <div className="text-xs text-slate-500">
                          {r?.criadoEm?.toDate
                            ? r.criadoEm.toDate().toLocaleDateString()
                            : ''}
                        </div>
                      </div>
                    </div>

                    {(r?.preset || r?.tags?.length) && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {r?.preset && <Pill tone="neutral">{r.preset}</Pill>}
                        {Array.isArray(r?.tags) &&
                          r.tags.slice(0, 6).map((t) => (
                            <Pill key={t} tone="neutral">
                              {t}
                            </Pill>
                          ))}
                      </div>
                    )}

                    {r?.comentario && (
                      <p className="mt-3 text-sm text-slate-700 leading-relaxed">
                        {r.comentario}
                      </p>
                    )}

                    <div className="mt-4 flex flex-wrap gap-2">
                      <Pill tone="neutral">
                        Ética: {clamp01(c.etica).toFixed(0)}
                      </Pill>
                      <Pill tone="neutral">
                        Compromisso: {clamp01(c.compromisso).toFixed(0)}
                      </Pill>
                      <Pill tone="neutral">
                        Comunicação: {clamp01(c.comunicacao).toFixed(0)}
                      </Pill>
                      <Pill tone="neutral">
                        Postura: {clamp01(c.postura).toFixed(0)}
                      </Pill>
                      <Pill tone="neutral">
                        Resposta: {clamp01(c.tempoResposta).toFixed(0)}
                      </Pill>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="mt-3 text-xs text-slate-500">
            Sócios avaliam no perfil público do corretor (quando há sociedade
            aceita).
          </div>
        </>
      )}
    </section>
  );
}

/* =========================
   Página: PerfilPessoal
========================= */
const Perfil = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();

  const [dados, setDados] = useState(null);
  const [imoveis, setImoveis] = useState([]);
  const [qtdSocios, setQtdSocios] = useState(0);
  const [qtdParcerias, setQtdParcerias] = useState(0);
  const [incorporadoras, setIncorporadoras] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ Contador de placas
  const [qtdPlacas, setQtdPlacas] = useState(0);

  // 🔁 Modal de encerramento
  const [openEncerrar, setOpenEncerrar] = useState(false);
  const [imovelSelecionado, setImovelSelecionado] = useState(null);

  // ✅ UI: quando clica no Brazão, some imóveis e entra avaliações
  const [modo, setModo] = useState('imoveis'); // 'imoveis' | 'avaliacoes'

  // ✅ Stats do Brazão
  const [brazaoStats, setBrazaoStats] = useState({ count: 0, media: 0 });

  // ✅ Destaques (lidos do users/{uid})
  const [featuredIds, setFeaturedIds] = useState([]);
  const featuredSet = useMemo(
    () => new Set((featuredIds || []).map((x) => String(x))),
    [featuredIds]
  );

  const fetchImoveis = async () => {
    if (!usuario) return;
    try {
      const imoveisRef = collection(db, 'imoveis');

      // Sócios aceitos (sou criador ou convidado)
      const qS1 = query(
        collection(db, 'socios'),
        where('status', '==', 'accepted'),
        where('criador', '==', usuario.uid)
      );
      const qS2 = query(
        collection(db, 'socios'),
        where('status', '==', 'accepted'),
        where('convidado', '==', usuario.uid)
      );

      const [snapS1, snapS2] = await Promise.all([getDocs(qS1), getDocs(qS2)]);
      const sociosUids = Array.from(
        new Set(
          [...snapS1.docs, ...snapS2.docs].map((d) => {
            const data = d.data();
            return data.criador === usuario.uid ? data.convidado : data.criador;
          })
        )
      );
      setQtdSocios(sociosUids.length);

      // Parcerias aceitas (modelo novo e legado)
      const qP1 = query(
        collection(db, 'parcerias'),
        where('status', '==', 'accepted'),
        where('criador', '==', usuario.uid)
      );
      const qP2 = query(
        collection(db, 'parcerias'),
        where('status', '==', 'accepted'),
        where('convidado', '==', usuario.uid)
      );
      const qP3 = query(
        collection(db, 'parcerias'),
        where('status', '==', 'accepted'),
        where('from', '==', usuario.uid)
      );
      const qP4 = query(
        collection(db, 'parcerias'),
        where('status', '==', 'accepted'),
        where('to', '==', usuario.uid)
      );

      const [snapP1, snapP2, snapP3, snapP4] = await Promise.all([
        getDocs(qP1),
        getDocs(qP2),
        getDocs(qP3),
        getDocs(qP4),
      ]);

      const parceirosUids = new Set();
      snapP1.docs.forEach((d) => parceirosUids.add(d.data()?.convidado));
      snapP2.docs.forEach((d) => parceirosUids.add(d.data()?.criador));
      snapP3.docs.forEach((d) => parceirosUids.add(d.data()?.to));
      snapP4.docs.forEach((d) => parceirosUids.add(d.data()?.from));
      setQtdParcerias(parceirosUids.size);

      // Meus imóveis (seu schema usa criadoPor.uid)
      const meusSnap = await getDocs(
        query(imoveisRef, where('criadoPor.uid', '==', usuario.uid))
      );
      const meusImoveis = meusSnap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          origem: 'meu',
          ownerId: data.criadoPor?.uid,
        };
      });

      // Imóveis dos sócios compartilhados comigo
      const sociosQueries = sociosUids.map((socioUid) =>
        getDocs(
          query(
            imoveisRef,
            where('criadoPor.uid', '==', socioUid),
            where('compartilharComSocios', '==', true)
          )
        )
      );

      const sociosSnaps = await Promise.all(sociosQueries);
      const imoveisSocios = sociosSnaps.flatMap((snap) =>
        snap.docs.map((docIm) => {
          const data = docIm.data();
          return {
            id: docIm.id,
            ...data,
            origem: 'socio',
            ownerId: data.criadoPor?.uid,
          };
        })
      );

      setImoveis([...meusImoveis, ...imoveisSocios]);
    } catch (err) {
      console.error('Erro ao buscar imóveis:', err);
      setImoveis([]);
    }
  };

  const fetchIncorporadoras = async () => {
    if (!usuario?.uid) return;
    try {
      const q = query(
        collection(db, 'incorporadoras'),
        where('usuarioId', '==', usuario.uid)
      );
      const snap = await getDocs(q);
      setIncorporadoras(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Erro ao buscar incorporadoras:', err);
      setIncorporadoras([]);
    }
  };

  const fetchPlacas = async () => {
    if (!usuario?.uid) return;
    try {
      const total = await contarImoveisComPlaca(usuario.uid);
      setQtdPlacas(Number(total) || 0);
    } catch (e) {
      console.error('Erro ao contar placas:', e);
      setQtdPlacas(0);
    }
  };

  // ✅ Convidar usuário sugerido para sociedade
  const handleConvidarSociedade = async (user) => {
    if (!usuario?.uid || !user?.uid || user.uid === usuario.uid) return;

    try {
      const qA = query(
        collection(db, 'socios'),
        where('criador', '==', usuario.uid),
        where('convidado', '==', user.uid)
      );
      const qB = query(
        collection(db, 'socios'),
        where('criador', '==', user.uid),
        where('convidado', '==', usuario.uid)
      );
      const [a, b] = await Promise.all([getDocs(qA), getDocs(qB)]);
      if (!a.empty || !b.empty) {
        alert('Convite já enviado ou sociedade já existente.');
        return;
      }

      const refSoc = await addDoc(collection(db, 'socios'), {
        criador: usuario.uid,
        convidado: user.uid,
        status: 'pending',
        criadoEm: serverTimestamp(),
      });

      await addDoc(collection(db, 'notificacoes'), {
        uidDestino: user.uid,
        tipo: 'sociedade',
        refId: refSoc.id,
        mensagem: 'Você recebeu um convite de sociedade.',
        usuarioOrigem: {
          uid: usuario.uid,
          nome: dados?.nome || 'Corretor',
          foto: dados?.fotoPerfil?.url || dados?.fotoPerfil || null,
        },
        visualizado: false,
        data: serverTimestamp(),
      });

      alert('Convite de sociedade enviado!');
    } catch (e) {
      console.error('Erro ao convidar sócio:', e);
      alert('Não foi possível enviar o convite.');
    }
  };

  // ✅ Listener de stats para o Brazão (contagem + média)
  useEffect(() => {
    if (!usuario?.uid) return;

    const ref = collection(db, 'avaliacoes', usuario.uid, 'itens');
    const q = query(ref, orderBy('criadoEm', 'desc'), limit(50));

    const unsub = onSnapshot(
      q,
      (snap) => {
        const itens = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (!itens.length) {
          setBrazaoStats({ count: 0, media: 0 });
          return;
        }
        let sum = 0;
        itens.forEach((r) => {
          const c = r?.criterios || {};
          sum += Number(r?.media ?? calcMedia(c));
        });
        setBrazaoStats({ count: itens.length, media: sum / itens.length });
      },
      (err) => {
        console.error('Erro ao ler stats do brazão:', err);
        setBrazaoStats({ count: 0, media: 0 });
      }
    );

    return () => unsub();
  }, [usuario?.uid]);

  useEffect(() => {
    let unsubUser = () => {};
    let ativo = true;

    (async () => {
      if (!usuario) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // ✅ Lê o user doc e pega FeaturedIds de lá
      unsubUser = onSnapshot(doc(db, 'users', usuario.uid), (docSnap) => {
        if (!ativo) return;
        if (docSnap.exists()) {
          const userData = docSnap.data();
          setDados(userData);

          const ids = getFeaturedIdsFromUserDoc(userData);
          setFeaturedIds(ids);
        } else {
          console.warn('Usuário não encontrado:', usuario.uid);
          setDados(null);
          setFeaturedIds([]);
        }
      });

      await Promise.all([fetchImoveis(), fetchIncorporadoras(), fetchPlacas()]);
      if (ativo) setLoading(false);
    })();

    return () => {
      ativo = false;
      unsubUser();
    };
  }, [usuario]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ injeta destaque no objeto do imóvel (pra UI não “perder” marcação antiga)
  const imoveisComDestaque = useMemo(() => {
    return (imoveis || []).map((p) => {
      const isFeatured = featuredSet.has(String(p?.id));
      return {
        ...p,
        // nomes redundantes pra bater com qualquer lógica existente no PropertyCard:
        isDestaque: isFeatured,
        destaque: isFeatured,
        featured: isFeatured,
      };
    });
  }, [imoveis, featuredSet]);

  const imoveisOrdenados = useMemo(() => {
    return [...imoveisComDestaque].sort((a, b) => {
      const ra = statusRank(a);
      const rb = statusRank(b);
      if (ra !== rb) return ra - rb;

      const ta = toMillis(
        a?.updatedAt ||
          a?.atualizadoEm ||
          a?.updatedEm ||
          a?.ultimaAtualizacao ||
          a?.createdAt ||
          a?.criadoEm ||
          a?.data
      );
      const tb = toMillis(
        b?.updatedAt ||
          b?.atualizadoEm ||
          b?.updatedEm ||
          b?.ultimaAtualizacao ||
          b?.createdAt ||
          b?.criadoEm ||
          b?.data
      );
      return tb - ta;
    });
  }, [imoveisComDestaque]);

  if (loading) return <p className="p-6">Carregando perfil...</p>;
  if (!dados)
    return (
      <p className="p-6">
        Nenhum dado de perfil encontrado. Por favor, complete seu cadastro.
      </p>
    );

  // Normaliza a nota para escala de 0-5 (os dados originais são 0-10)
  const mediaParaExibir = brazaoStats.media ? brazaoStats.media / 2 : 0;

  return (
    <div className="relative">
      <IconeComparador />

      {openEncerrar && imovelSelecionado && (
        <EncerrarAnuncioModal
          aberto={openEncerrar}
          imovel={imovelSelecionado}
          onClose={() => {
            setOpenEncerrar(false);
            setImovelSelecionado(null);
          }}
          onFinalizado={() => {
            setOpenEncerrar(false);
            setImovelSelecionado(null);
            fetchImoveis();
            fetchPlacas(); // ✅ atualiza placas após encerrar/alterar
          }}
        />
      )}

      <div className="max-w-7xl mx-auto flex p-0 m-0">
        <div className="flex-1 space-y-4 p-6 pt-[92px] bg-slate-50/40 min-h-[calc(100vh-92px)]">
          <div className="pb-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-start gap-6 flex-wrap">
              <img
                src={
                  dados.fotoPerfil?.url ||
                  dados.fotoPerfil ||
                  '/placeholder-user.jpg'
                }
                alt="Foto de perfil"
                className="w-24 h-24 rounded-full object-cover border border-slate-200"
              />

              <div className="flex-1 min-w-[200px]">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <h2 className="text-2xl font-semibold text-gray-900">
                      {dados.nome || 'Nome não informado'}
                    </h2>

                    <div
                      className="mt-1 cursor-pointer hover:opacity-80 transition-opacity inline-flex"
                      onClick={() =>
                        setModo((m) =>
                          m === 'avaliacoes' ? 'imoveis' : 'avaliacoes'
                        )
                      }
                      title="Clique para ver avaliações"
                    >
                      <BrazaoGoogleRating
                        rating={mediaParaExibir}
                        total={brazaoStats.count}
                        variant="gold"
                        size={16}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Link
                      to="/editar-perfil"
                      className="flex items-center gap-2 bg-blue-600 text-white text-sm px-4 py-2 rounded-xl hover:bg-blue-700 transition"
                    >
                      <Pencil size={16} /> Editar Perfil
                    </Link>
                  </div>
                </div>

                {/* ✅ Contadores (com Placas) */}
                <div className="flex gap-6 text-sm text-gray-800 mt-3 flex-wrap">
                  <span
                    className="cursor-pointer hover:text-blue-600 transition"
                    onClick={() => navigate('/socios')}
                  >
                    <strong>{qtdSocios}</strong> Sócios
                  </span>

                  <span
                    className="cursor-pointer hover:text-blue-600 transition"
                    onClick={() => navigate('/parcerias')}
                  >
                    <strong>{qtdParcerias}</strong> Parcerias
                  </span>

                  <span>
                    <strong>{imoveis.length}</strong> Imóveis
                  </span>

                  <span title="Total de imóveis com placa ativada">
                    <strong>{qtdPlacas}</strong> Placas
                  </span>

                  {/* ✅ opcional: mostrar quantos destaques já tem */}
                  <span title="Quantidade de imóveis em destaque no site">
                    <strong>{featuredIds.length}</strong> Destaques
                  </span>
                </div>

                <div className="mt-3 text-sm text-gray-600 space-y-1">
                  {dados.especialidade && (
                    <div className="flex items-center gap-2">
                      <Building2 size={16} />
                      {formatarEspecialidade(dados.especialidade)}
                    </div>
                  )}
                  {dados.endereco?.cidade && (
                    <div className="flex items-center gap-2">
                      <MapPin size={16} /> {dados.endereco?.cidade}
                      {dados.endereco?.estado
                        ? `, ${dados.endereco?.estado}`
                        : ''}
                    </div>
                  )}
                  {dados.creci && (
                    <div className="flex items-center gap-2">
                      <BadgePercent size={16} /> CRECI: {dados.creci}
                    </div>
                  )}
                  {dados.telefone && (
                    <a
                      href={`https://wa.me/55${dados.telefone.replace(
                        /\D/g,
                        ''
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#1e293b] hover:text-black font-medium transition"
                    >
                      <Phone size={16} /> Conversar no WhatsApp
                    </a>
                  )}
                </div>

                {dados.descricao && (
                  <p className="pt-3 text-sm text-gray-700 leading-relaxed max-w-3xl">
                    {dados.descricao}
                  </p>
                )}
              </div>
            </div>
          </div>

          {incorporadoras?.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-gray-700">
                  Empresas parceiras
                </h3>
                <Pill tone="neutral">
                  <ShieldCheck size={14} />
                  {incorporadoras.length}
                </Pill>
              </div>

              <div className="mt-4">
                <ListaIncorporadoras
                  items={incorporadoras}
                  onEdit={(item) => console.log('Editar', item?.id)}
                  onDeleted={(id) =>
                    setIncorporadoras((prev) => prev.filter((i) => i.id !== id))
                  }
                />
              </div>
            </div>
          )}

          {modo === 'avaliacoes' ? (
            <AvaliacoesPanel
              perfilId={usuario?.uid}
              nomePerfil={dados?.nome}
              onVoltar={() => setModo('imoveis')}
            />
          ) : imoveisOrdenados.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <p className="text-slate-600 text-sm">
                Nenhum imóvel cadastrado.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              {imoveisOrdenados.map((imovel) => (
                <PropertyCard
                  key={imovel.id}
                  property={imovel}
                  // ✅ mantém destaque sincronizado no estado local (evita “desmarcar” visual)
                  onUpdateLocal={(id, patch) => {
                    setImoveis((prev) =>
                      prev.map((p) => (p.id === id ? { ...p, ...patch } : p))
                    );
                  }}
                  onEncerrar={(prop) => {
                    setImovelSelecionado(prop);
                    setOpenEncerrar(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="w-64 mt-[92px] pr-6">
          <SugestoesAside
            title="Sugestões de sociedade"
            limit={6}
            onConvidar={handleConvidarSociedade}
            onIgnorar={(user) => console.log('Ignorar sugestão de', user?.uid)}
          />
        </div>
      </div>
    </div>
  );
};

export default Perfil;
